/**
 * GNSS Quota Display Component
 * 
 * Shows current usage against quota limits
 */

import React, { useState, useEffect } from 'react';
import { GNSSUserQuota } from '../services/gnssAuthService';
import gnssAuthService from '../services/gnssAuthService';

interface GNSSQuotaDisplayProps {
  quota?: GNSSUserQuota;
  compact?: boolean;
}

const GNSSQuotaDisplay: React.FC<GNSSQuotaDisplayProps> = ({ quota, compact = false }) => {
  const [displayQuota, setDisplayQuota] = useState<GNSSUserQuota | null>(quota || null);

  useEffect(() => {
    if (!quota) {
      const stored = gnssAuthService.getStoredApiKey();
      if (stored) {
        setDisplayQuota(gnssAuthService.getUserQuota(stored.tier));
      }
    }
  }, [quota]);

  if (!displayQuota) return null;

  const usage = gnssAuthService.getUsagePercentage(displayQuota);
  const resetDate = new Date(displayQuota.monthlyResetDate);
  const daysUntilReset = Math.ceil((resetDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));

  const getProgressColor = (percentage: number) => {
    if (percentage < 50) return 'bg-green-600';
    if (percentage < 80) return 'bg-yellow-600';
    return 'bg-red-600';
  };

  const getTierBadge = () => {
    const tierColors = {
      free: 'bg-gray-600 text-gray-100',
      pro: 'bg-blue-600 text-blue-100',
      enterprise: 'bg-purple-600 text-purple-100',
    };
    return tierColors[displayQuota.tier];
  };

  if (compact) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-400">Jobs</span>
          <span className={`${getProgressColor(usage.jobs)} text-white px-2 py-1 rounded text-xs font-bold`}>
            {displayQuota.monthlyJobsUsed}/{displayQuota.monthlyJobsLimit}
          </span>
        </div>
        <div className="w-full bg-gray-700 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all ${getProgressColor(usage.jobs)}`}
            style={{ width: `${Math.min(usage.jobs, 100)}%` }}
          />
        </div>

        <div className="flex items-center justify-between text-xs mt-3">
          <span className="text-gray-400">Data</span>
          <span className={`${getProgressColor(usage.data)} text-white px-2 py-1 rounded text-xs font-bold`}>
            {displayQuota.monthlyDataUsed.toFixed(1)}/{displayQuota.monthlyDataLimit} MB
          </span>
        </div>
        <div className="w-full bg-gray-700 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all ${getProgressColor(usage.data)}`}
            style={{ width: `${Math.min(usage.data, 100)}%` }}
          />
        </div>

        <div className="text-xs text-gray-500 text-center mt-2">
          Resets in {daysUntilReset} days
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-100">Monthly Quota</h3>
        <span className={`px-3 py-1 rounded-full text-xs font-bold ${getTierBadge()}`}>
          {displayQuota.tier.charAt(0).toUpperCase() + displayQuota.tier.slice(1)} Tier
        </span>
      </div>

      {/* Jobs Quota */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-300">Processing Jobs</span>
          <span className="text-sm font-mono text-gray-400">
            {displayQuota.monthlyJobsUsed} / {displayQuota.monthlyJobsLimit}
          </span>
        </div>
        <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
          <div
            className={`h-3 rounded-full transition-all ${getProgressColor(usage.jobs)}`}
            style={{ width: `${Math.min(usage.jobs, 100)}%` }}
          />
        </div>
        <p className="text-xs text-gray-500 mt-1">
          {displayQuota.monthlyJobsLimit - displayQuota.monthlyJobsUsed} jobs remaining
        </p>
      </div>

      {/* Data Quota */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-300">Data Processed</span>
          <span className="text-sm font-mono text-gray-400">
            {displayQuota.monthlyDataUsed.toFixed(1)} / {displayQuota.monthlyDataLimit} MB
          </span>
        </div>
        <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
          <div
            className={`h-3 rounded-full transition-all ${getProgressColor(usage.data)}`}
            style={{ width: `${Math.min(usage.data, 100)}%` }}
          />
        </div>
        <p className="text-xs text-gray-500 mt-1">
          {(displayQuota.monthlyDataLimit - displayQuota.monthlyDataUsed).toFixed(1)} MB remaining
        </p>
      </div>

      {/* Concurrent Jobs */}
      <div className="pt-2 border-t border-gray-700">
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <p className="text-xs text-gray-400">Concurrent Jobs</p>
            <p className="font-mono text-gray-100">{displayQuota.concurrentJobsLimit}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400">Max Duration</p>
            <p className="font-mono text-gray-100">{Math.floor(displayQuota.maxJobDuration / 3600)}h</p>
          </div>
        </div>
      </div>

      {/* Reset Info */}
      <div className="bg-gray-900/50 rounded p-3">
        <p className="text-xs text-gray-400">
          Your quota resets on <span className="text-gray-200 font-mono">{resetDate.toLocaleDateString()}</span> ({daysUntilReset} days)
        </p>
      </div>

      {/* Warnings */}
      {usage.jobs > 80 && (
        <div className="bg-yellow-900/30 border border-yellow-700 rounded p-3">
          <p className="text-xs text-yellow-200">
            ⚠️ You're approaching your job limit
          </p>
        </div>
      )}
      {usage.data > 80 && (
        <div className="bg-yellow-900/30 border border-yellow-700 rounded p-3">
          <p className="text-xs text-yellow-200">
            ⚠️ You're approaching your data limit
          </p>
        </div>
      )}
      {usage.jobs >= 100 || usage.data >= 100 && (
        <div className="bg-red-900/30 border border-red-700 rounded p-3">
          <p className="text-xs text-red-200">
            ❌ You've reached your quota limit
          </p>
        </div>
      )}
    </div>
  );
};

export default GNSSQuotaDisplay;
